import type { AxiosResponse } from 'axios';
import axios from 'axios';

import { useSessionStorage } from 'shared/src/composables/storage/useSessionStorage';
import type { LogoutType } from 'shared/clients/client-spa-AccountGroup';
import { DESTINATION_APP_TYPE, MESSAGE_TYPE } from 'shared/src/composables/messages/messages.model';
import { useMessages } from 'shared/src/composables/messages/useMessages.composable';
import type { RemovableRef } from 'shared/src/composables/storage/shared';
import type { SsoConfig } from 'shared/typings/appSettings';
import { getPropertyFromJwt } from 'shared/src/helpers/jwt.helper';

import { refreshPage } from './refreshPage';

class AuthorizationService {
  private orgId: RemovableRef<string | null> = useSessionStorage('orgId', null);
  private delegationPrincipalPesel: RemovableRef<string | null> = useSessionStorage('delegationPrincipalPesel', null);
  private delegationPrincipalNip: RemovableRef<string | null> = useSessionStorage('delegationPrincipalNip', null);
  private eWtId: RemovableRef<string | null> = useSessionStorage('eWtId', null);

  private useMessages = useMessages();

  public loginAsNaturalPerson(): void {
    this.clearOrgId();
    this.clearDelegationPrincipalPesel();
    this.clearDelegationPrincipalNip();
    refreshPage();
  }

  public loginAsOrganization(organizationNip: string): void {
    this.clearDelegationPrincipalPesel();
    this.clearDelegationPrincipalNip();
    this.orgId.value = organizationNip;
    refreshPage();
  }

  public loginAsDelegationUser(principalPesel: string): void {
    this.clearOrgId();
    this.clearDelegationPrincipalNip();
    this.delegationPrincipalPesel.value = principalPesel;
    refreshPage();
  }

  public loginAsDelegationOrganization(principalNip: string): void {
    this.clearOrgId();
    this.clearDelegationPrincipalPesel();
    this.delegationPrincipalNip.value = principalNip;
    refreshPage();
  }

  public clearOrgId = (): void => {
    this.orgId.value = null;
  };

  public clearDelegationPrincipalPesel = (): void => {
    this.delegationPrincipalPesel.value = null;
  };

  public clearDelegationPrincipalNip = (): void => {
    this.delegationPrincipalNip.value = null;
  };

  public clearEWtId = (): void => {
    this.eWtId.value = null;
  };

  public postLogoutMessage = (logoutType: LogoutType): void => {
    this.useMessages.postWindowMessage(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.LOGOUT, logoutType);
  };

  public async getTokenForChatBot(ssoConfig: SsoConfig, token: string | undefined): Promise<AxiosResponse> {
    const params = {
      'grant_type': 'scopeReduceTokenExchange',
      'token': token ?? '',
      'client_id': ssoConfig.ClientId,
      'scope': 'ChatApi.UserAccess openid profile',
    };

    return axios.post(`${window.appSettings.Sso.Authority}/connect/token`, new URLSearchParams(params).toString());
  }

  public async getTokenForMakeAnAppointment(ssoConfig: SsoConfig, token: string): Promise<AxiosResponse> {
    const clientId = getPropertyFromJwt(token, 'client_id');

    const params = {
      'grant_type': 'audienceChange',
      'subject_token': token,
      'client_id': clientId as string,
      'audience_client_id': ssoConfig.Clients.ScheduleVisit.Id,
      'audience': ssoConfig.Clients.ScheduleVisit.Audience!,
      'audience_scope': ssoConfig.Clients.ScheduleVisit.Scopes!,
    };

    return axios.post(`${window.appSettings.Sso.Authority}/connect/token`, new URLSearchParams(params).toString());
  }
}

export default new AuthorizationService();
